/**
 * Etapa 4 — Multas e juros.
 *
 * Define como sera aplicada a multa por atraso. 4 modalidades:
 *  - Parcela extra (anexa parcela ao final)
 *  - Percentual sobre o valor (com escolha de aplicar sobre toda a divida ou parcela)
 *  - Valor fixo
 *  - Nao aplicar
 *
 * Movido aqui da Etapa 2 (Regras) para isolar configuracao financeira.
 */

import type { Dispatch, SetStateAction } from "react";
import { Field, MoneyInput, PercentInput, Select } from "../components/Field";
import { InfoCallout, SectionCard } from "../components/SectionCard";
import { Radio } from "../components/Toggle";
import type { PlanFormState } from "../types";
import type { ChargePlanTipoMulta } from "../../../../services/charge-plans.service";

interface Props {
  state: PlanFormState;
  setState: Dispatch<SetStateAction<PlanFormState>>;
}

export function Step4Fines({ state, setState }: Props) {
  const set = <K extends keyof PlanFormState>(key: K, value: PlanFormState[K]) =>
    setState((prev) => ({ ...prev, [key]: value }));

  return (
    <div className="flex flex-col gap-6">
      <header>
        <h1 className="text-2xl font-bold text-foreground">4. Multas e juros</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Defina como sera cobrada a multa quando houver atraso no pagamento.
        </p>
      </header>

      <SectionCard
        icon={<PercentIcon />}
        title="Tipo de multa por atraso"
        description="Escolha como sera aplicada a multa quando houver atraso no pagamento."
      >
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[260px_1fr]">
          <div className="flex flex-col gap-4">
            <FineOption
              checked={state.tipoMulta === "parcela_extra"}
              onClick={() => set("tipoMulta", "parcela_extra")}
              title="Parcela extra"
              desc="E adicionada ao final do contrato mais uma parcela de multa sempre que o cliente atrasar."
            />
            <FineOption
              checked={state.tipoMulta === "percentual"}
              onClick={() => set("tipoMulta", "percentual")}
              title="Percentual sobre o valor"
              desc="A multa sera um percentual sobre o valor da cobranca."
            />
            <FineOption
              checked={state.tipoMulta === "valor_fixo"}
              onClick={() => set("tipoMulta", "valor_fixo")}
              title="Valor fixo"
              desc="A multa sera um valor fixo."
            />
            <FineOption
              checked={state.tipoMulta === "nenhuma"}
              onClick={() => set("tipoMulta", "nenhuma")}
              title="Nao aplicar multa"
              desc="Nao sera aplicada multa por atraso."
            />
          </div>

          <FineDetails
            tipoMulta={state.tipoMulta}
            multaPercentual={state.multaPercentual}
            multaValorFixo={state.multaValorFixo}
            multaAplicarSobre={state.multaAplicarSobre}
            multaExplicacao={state.multaExplicacao}
            onChange={(patch) => setState((prev) => ({ ...prev, ...patch }))}
          />
        </div>

        <div className="mt-6">
          <InfoCallout>
            <strong>Como o cliente vera:</strong> em caso de atraso, a multa
            sera aplicada conforme a regra definida acima.
          </InfoCallout>
        </div>
      </SectionCard>
    </div>
  );
}

// ====== Sub-componentes ======

function FineOption({
  checked,
  onClick,
  title,
  desc,
}: {
  checked: boolean;
  onClick: () => void;
  title: string;
  desc: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-start gap-3 rounded-xl border p-3 text-left transition-colors ${
        checked
          ? "border-primary-300 bg-primary-50/50"
          : "border-transparent hover:bg-muted"
      }`}
    >
      <div className="mt-0.5">
        <Radio checked={checked} onChange={onClick} />
      </div>
      <div>
        <h4 className="text-sm font-bold text-foreground">{title}</h4>
        <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{desc}</p>
      </div>
    </button>
  );
}

function FineDetails({
  tipoMulta,
  multaPercentual,
  multaValorFixo,
  multaAplicarSobre,
  multaExplicacao,
  onChange,
}: {
  tipoMulta: ChargePlanTipoMulta;
  multaPercentual: string;
  multaValorFixo: string;
  multaAplicarSobre: string;
  multaExplicacao: string;
  onChange: (patch: Partial<PlanFormState>) => void;
}) {
  if (tipoMulta === "nenhuma") {
    return (
      <div className="flex items-center justify-center rounded-xl border border-dashed border-border p-6 text-sm text-muted-foreground">
        Nenhuma multa sera aplicada.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {tipoMulta === "parcela_extra" ? (
        <Field
          label="Explicacao para o cliente (opcional)"
          hint="Esta descricao aparece no app do cliente."
        >
          <textarea
            value={multaExplicacao}
            onChange={(e) => onChange({ multaExplicacao: e.target.value })}
            rows={3}
            className="w-full rounded-lg border border-input bg-card p-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </Field>
      ) : null}

      {tipoMulta === "percentual" ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field label="Percentual da multa (%)">
            <PercentInput
              value={multaPercentual}
              onChange={(v) => onChange({ multaPercentual: v })}
            />
          </Field>
          <Field label="Aplicar sobre">
            <Select
              value={multaAplicarSobre}
              onChange={(v) => onChange({ multaAplicarSobre: v })}
              options={[
                { value: "sobre_toda_divida", label: "Sobre toda a divida" },
                { value: "sobre_parcela", label: "Sobre a parcela em atraso" },
              ]}
            />
          </Field>
        </div>
      ) : null}

      {tipoMulta === "valor_fixo" ? (
        <Field label="Valor da multa">
          <MoneyInput
            value={multaValorFixo}
            onChange={(v) => onChange({ multaValorFixo: v })}
          />
        </Field>
      ) : null}
    </div>
  );
}

// ====== Icones ======

function PercentIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="19" y1="5" x2="5" y2="19" />
      <circle cx="6.5" cy="6.5" r="2.5" />
      <circle cx="17.5" cy="17.5" r="2.5" />
    </svg>
  );
}
